import React, { useEffect, useState } from "react";
import { api, fmtEur, fmtInt } from "@/lib/api";
import { PageHeader, Section, Pill, EmptyState } from "@/components/ui-kit";
import { Download, Package } from "lucide-react";

export default function Stock() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/stock/summary").then(({ data }) => setRows(data));
  }, []);

  const filtered = rows.filter(r => {
    if (!q) return true;
    const s = q.toLowerCase();
    return r.title.toLowerCase().includes(s) || (r.isbn || "").includes(s);
  });

  function downloadCsv() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/stock.csv`;
    const token = localStorage.getItem("access_token");
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "stock.csv"; a.click();
      });
  }
  function downloadXlsx() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/stock.xlsx`;
    const token = localStorage.getItem("access_token");
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "stock.xlsx"; a.click();
      });
  }

  return (
    <div data-testid="stock-page">
      <PageHeader
        title="Almacén & Stock"
        subtitle="Stock consolidado por título: almacén propio, distribuidor y depósito en clientes."
        actions={
          <div className="flex gap-2">
            <button onClick={downloadCsv} data-testid="stock-export-csv" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">
              <Download size={15} strokeWidth={1.5} /> CSV
            </button>
            <button onClick={downloadXlsx} data-testid="stock-export-xlsx" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">
              <Download size={15} strokeWidth={1.5} /> Excel
            </button>
          </div>
        }
      />

      <div className="mb-5 max-w-md">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrar por título o ISBN…"
          data-testid="stock-filter-input"
          className="w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
        />
      </div>

      <Section>
        {filtered.length === 0 ? (
          <EmptyState title="Sin stock registrado" description="Añade títulos al catálogo y registra movimientos de entrada para ver el stock consolidado." testid="stock-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stock-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                  <th className="text-left px-5 py-3 font-medium">ISBN</th>
                  <th className="text-left px-5 py-3 font-medium">Título</th>
                  <th className="text-right px-5 py-3 font-medium">PVP</th>
                  <th className="text-right px-5 py-3 font-medium" title="Stock físico disponible en tu almacén">Propio</th>
                  <th className="text-right px-5 py-3 font-medium" title="En depósito distribuidor (no vendido)">Distribuidor</th>
                  <th className="text-right px-5 py-3 font-medium" title="En depósito en cliente (librería/escuela)">Depósito</th>
                  <th className="text-right px-5 py-3 font-medium text-olive" title="Ventas históricas (directa + feria + liquidación)">Vendido</th>
                  <th className="text-right px-5 py-3 font-medium text-amber" title="Devoluciones re-entradas al almacén">Devuelto</th>
                  <th className="text-right px-5 py-3 font-medium text-crimson" title="Deterioro + promocional">Baja</th>
                  <th className="text-right px-5 py-3 font-medium">Vivo</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.book_id} className="border-t border-line hover:bg-sidebar/30" data-testid={`stock-row-${r.isbn}`}>
                    <td className="px-5 py-3 font-mono text-xs text-ink2">{r.isbn}</td>
                    <td className="px-5 py-3">{r.title}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtEur(r.pvp)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(r.total_propio)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(r.total_distribuidor)}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(r.total_deposito_cliente)}</td>
                    <td className="px-5 py-3 text-right font-mono text-olive">{fmtInt(r.total_vendido)}</td>
                    <td className="px-5 py-3 text-right font-mono text-amber">{fmtInt(r.total_devuelto)}</td>
                    <td className="px-5 py-3 text-right font-mono text-crimson">{fmtInt(r.total_baja)}</td>
                    <td className="px-5 py-3 text-right">
                      <Pill tone={r.total > 0 ? "success" : "muted"}>{fmtInt(r.total)}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="mt-4 text-xs text-ink2 leading-relaxed max-w-3xl">
        <strong className="text-ink">Regla de stock:</strong> enviar a distribuidor o a depósito <em>no</em> es una venta, solo un traslado (sale de Propio, entra en Distribuidor/Depósito).
        Solo cuentan como <span className="text-olive">venta real</span> los movimientos <code>venta_directa</code>, <code>venta_feria</code> y <code>liquidacion_venta</code> (generada al confirmar la liquidación mensual).
        <em>Vivo</em> = Propio + Distribuidor + Depósito. Vendido, Devuelto y Baja son acumulados históricos, no stock actual.
      </div>
    </div>
  );
}
